import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Get token from authStore
const getToken = () => {
  return useAuthStore.getState().token;
};

export interface LearningContentVersion {
  _id?: string;
  version: string; // Format: 'userEmail-topicTitle-version x'
  title: string;
  htmlContent: string;
  userId: string;
  userEmail: string;
  timeStamp: string;
  isLatest: boolean;
}

export interface Exercise {
  _id?: string;
  type: 'quiz' | 'practice' | 'demonstration';
  title?: string;
  content?: any;
  order?: number;
}

export interface Topic {
  _id: string;
  id: string;
  title: string;
  slug: string;
  description: string;
  category: {
    _id: string;
    name: string;
    slug: string;
  };
  subcategory?: {
    _id: string;
    name?: string;
    slug?: string;
  } | string;
  subcategoryCode?: string;
  subcategoryName?: string;
  content?: any;
  learningContent?: LearningContentVersion[];
  keywords?: any[];
  prerequisites?: any[];
  exercises?: Exercise[];
  published: boolean;
  order: number;
}

interface TopicsResponse {
  topics: Topic[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Get all topics
export const getAllTopics = async (filters?: {
  category?: string;
  published?: boolean;
  page?: number;
  limit?: number;
  full?: boolean; // Set to true to include heavy fields (learningContent, content, exercises)
}): Promise<TopicsResponse> => {
  const token = getToken();

  const params = new URLSearchParams();
  if (filters?.category) params.append('category', filters.category);
  if (filters?.published !== undefined) params.append('published', String(filters.published));
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.full !== undefined) params.append('full', String(filters.full));

  const response = await axios.get(
    `${API_BASE_URL}/topics?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get topic by ID
export const getTopicById = async (topicId: string): Promise<Topic> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/topics/id/${topicId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data.topic;
};

// Get topic by slug
export const getTopicBySlug = async (slug: string): Promise<Topic> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/topics/${slug}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data.topic;
};

// Update topic
export const updateTopic = async (topicId: string, updates: {
  htmlContent?: string;
  saveAsNewVersion?: boolean;
  versionTitle?: string;
  userEmail?: string;
  learningContent?: LearningContentVersion[];
  content?: any;
  title?: string;
  description?: string;
  category?: string;
  published?: boolean;
  order?: number;
  exercises?: Exercise[];
}): Promise<Topic> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/topics/${topicId}`,
    updates,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data.topic;
};

/**
 * Save exercise HTML as a NEW VERSION.
 *
 * Sending `exercises: [...]` through updateTopic REPLACES the array and
 * destroys the history, so exercise edits must go through this path instead.
 * The server appends the version and clears isLatest on the previous ones.
 */
export const saveExerciseVersion = async (
  topicId: string,
  data: { exerciseHtml: string; title?: string; userEmail?: string }
): Promise<Topic> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/topics/${topicId}`,
    {
      saveExerciseAsNewVersion: true,
      exerciseHtml: data.exerciseHtml,
      exerciseTitle: data.title,
      userEmail: data.userEmail
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data.topic;
};

// Create topic
export const createTopic = async (topicData: {
  title: string;
  slug: string;
  description: string;
  category: string;
  htmlContent?: string | Array<{
    version: string;
    title: string;
    htmlContent: string;
    isLatest?: boolean;
    createdAt?: string;
  }>;
  learningContent?: Array<{
    version: string;
    title: string;
    htmlContent: string;
    userId: string;
    userEmail: string;
    timeStamp?: string;
    isLatest?: boolean;
  }>;
  content?: any;
  published?: boolean;
  order?: number;
}): Promise<Topic> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/topics`,
    topicData,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data.topic;
};

// Delete topic
export const deleteTopic = async (topicId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/topics/${topicId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

// ==================== Categories API ====================

export interface Category {
  _id: string;
  code: string;
  name: string;
  chinese_name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  topics_count: number;
  order: number;
  parentCategory?: Category | null;
  subcategories?: Category[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CategoriesResponse {
  categories: Category[];
}

// Get all categories (hierarchical structure by default)
export const getAllCategories = async (options?: {
  hierarchy?: boolean;
  isActive?: boolean;
}): Promise<CategoriesResponse> => {
  const token = getToken();

  const params = new URLSearchParams();
  if (options?.hierarchy !== undefined) params.append('hierarchy', String(options.hierarchy));
  if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));

  const response = await axios.get(
    `${API_BASE_URL}/categories?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get category by code
export const getCategoryByCode = async (code: string): Promise<{ category: Category }> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/categories/code/${code}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get category by slug
export const getCategoryBySlug = async (slug: string): Promise<{ category: Category }> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/categories/${slug}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Create main category
export const createMainCategory = async (categoryData: {
  code: string;
  name: string;
  chinese_name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  topics_count?: number;
  order?: number;
}): Promise<{ category: Category }> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/categories/main`,
    categoryData,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Create subcategory
export const createSubcategory = async (subcategoryData: {
  parentCategoryCode: string;
  code: string;
  name: string;
  chinese_name: string;
  slug: string;
  description?: string;
  topics_count?: number;
  order?: number;
}): Promise<{ category: Category }> => {
  const token = getToken();
  const response = await axios.post(
    `${API_BASE_URL}/categories/subcategory`,
    subcategoryData,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Update category
export const updateCategory = async (
  categoryId: string,
  updates: Partial<Omit<Category, '_id' | 'createdAt' | 'updatedAt'>>
): Promise<{ category: Category }> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/categories/${categoryId}`,
    updates,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Delete category (soft delete)
export const deleteCategory = async (categoryId: string): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/categories/${categoryId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};
